import axios from "axios";
import { APIBlobResponse, APIResponse } from "../models/NetworkResponse";

interface Params {
  baseUrl: string;
  headers: any;
  method: string;
}

const postConfig = (token?: string | null): Params => {
  return {
    baseUrl: process.env.REACT_APP_API_URL || "",
    headers: {
      Authorization: token,
    },
    method: "post",
  };
};

const getConfig = (token?: string | null): Params => {
  return {
    baseUrl: process.env.REACT_APP_API_URL || "",
    headers: {
      Authorization: token,
    },
    method: "get",
  };
};

const getAnalyticsConfig = (token?: string | null): Params => {
  return {
    baseUrl:
      process.env.REACT_APP_ANALYTICS_API_URL || process.env.REACT_APP_API_URL || "",
    headers: {
      Authorization: token,
    },
    method: "get",
  };
};

const postAnalyticsConfig = (token?: string | null): Params => {
  return {
    baseUrl:
      process.env.REACT_APP_ANALYTICS_API_URL || process.env.REACT_APP_API_URL || "",
    headers: {
      Authorization: token,
    },
    method: "post",
  };
};

const patchConfig = (token?: string | null): Params => {
  return {
    baseUrl: process.env.REACT_APP_API_URL || "",
    headers: {
      Authorization: token,
    },
    method: "patch",
  };
};
const putConfig = (token?: string | null): Params => {
  return {
    baseUrl: process.env.REACT_APP_API_URL || "",
    headers: {
      Authorization: token,
    },
    method: "put",
  };
};

const deleteConfig = (token?: string | null): Params => {
  return {
    baseUrl: process.env.REACT_APP_API_URL || "",
    headers: {
      Authorization: token,
    },
    method: "delete",
  };
};

const uploadFileConfig = (token?: string | null): Params => {
  return {
    baseUrl: process.env.REACT_APP_API_URL || "",
    headers: {
      Authorization: token,
      "Content-Type": "multipart/form-data",
    },
    method: "post",
  };
};

const createQueryParams = (params?: any): string | null => {
  if (!params) return null;

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, encodeURIComponent(String(value)));
    }
  });

  return searchParams.toString();
};

export const postAPI = async (
  url: string,
  data?: any,
  signal?: AbortSignal,
): Promise<APIResponse> => {
  try {
    const config = postConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      data,
      ...(signal && { signal }),
    });
    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const getAPI = async (
  url: string,
  params?: any,
): Promise<APIResponse> => {
  try {
    const query_string = createQueryParams(params);
    const config = getConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url:
        query_string && query_string !== ""
          ? `${config.baseUrl}${url}?${query_string}`
          : `${config.baseUrl}${url}`,
    });
    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const getAnalyticsAPI = async (
  url: string,
  params?: any,
): Promise<APIResponse> => {
  try {
    const query_string = createQueryParams(params);
    const config = getAnalyticsConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url:
        query_string && query_string !== ""
          ? `${config.baseUrl}${url}?${query_string}`
          : `${config.baseUrl}${url}`,
    });
    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const postAnalyticsAPI = async (
  url: string,
  data?: any,
  signal?: AbortSignal,
): Promise<APIResponse> => {
  try {
    const config = postAnalyticsConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      data,
      ...(signal && { signal }),
    });
    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const putAPI = async (url: string, data?: any): Promise<APIResponse> => {
  try {
    const config = putConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      data,
    });
    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const patchAPI = async (
  url: string,
  data?: any,
): Promise<APIResponse> => {
  try {
    const config = patchConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      data,
    });
    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const postMultipartAPI = async (
  url: string,
  formData: FormData,
): Promise<APIResponse> => {
  try {
    const config = uploadFileConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      data: formData,
    });
    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const deleteAPI = async (
  url: string,
  body?: Record<string, any>,
): Promise<APIResponse> => {
  try {
    const config = deleteConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      ...(body && { data: body }),
    });
    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const postBlobAPI = async (url: string, data: any): Promise<Blob> => {
  try {
    const config = postConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      baseURL: process.env.REACT_APP_API_URL || "",
      headers: {
        Authorization: localStorage.getItem(
          process.env.REACT_APP_TOKEN_KEY || "",
        ),
      },
      method: "post",
      url: `${config.baseUrl}${url}`,
      data,
    });

    return response.data as Blob;
  } catch (error) {
    throw error;
  }
};

export const getBlobAPI = async (url: string): Promise<Blob> => {
  try {
    const config = getConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      responseType: "blob",
    });

    // Check if the response is actually a blob by checking content-type
    const contentType = response.headers["content-type"];
    if (contentType && typeof contentType === 'string' && contentType.includes("application/json")) {
      // If it's JSON, it's likely an error response
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      throw new Error(errorData.message || "Download failed");
    }

    return response.data as Blob;
  } catch (error) {
    // If it's already an Error object, re-throw it
    // if (error instanceof Error) {
    //   throw error;
    // }

    // If it's an axios error with response, try to parse JSON error
    if (axios.isAxiosError(error) && error.response?.data) {
      let errorData: any = {};
      try {
        // Try to read the error response as text first
        const errorText = await error.response.data.text();
        errorData = JSON.parse(errorText);
      } catch (parseError) {
        // If parsing fails, throw the original error
        throw new Error("Download failed");
      }

      throw new Error(errorData.message || "Download failed");
    } else {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Download failed");
    }
  }
};

export const downloadCSV = async (url: string): Promise<APIBlobResponse> => {
  try {
    const config = getConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      responseType: "blob",
    });
    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"] ||
      "";
    const list = contentDisposition.split("=") || [];
    const filename =
      list.length > 1 ? list[1].replace(/"/g, "").trim() : undefined;
    return {
      filename,
      data: new Blob([response.data], {
        type: (response.headers["content-type"] as string) || "text/csv;charset=utf-8",
      }),
    };
  } catch (error) {
    throw error;
  }
};

export const downloadPDF = async (url: string): Promise<APIBlobResponse> => {
  try {
    const config = getConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      responseType: "blob",
    });
    const list = response.headers["content-disposition"].split("=") || [];
    return {
      filename: list.length > 1 ? list[1] : undefined,
      data: new Blob([response.data], { type: "application/pdf" }),
    };
  } catch (error) {
    throw error;
  }
};

export const downloadFile = async (url: string): Promise<APIBlobResponse> => {
  try {
    const config = getConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );

    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      responseType: "blob",
    });
    const list =
      (
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"]
      ).split("=") || [];
    return {
      filename: list.length > 1 ? list[1] : undefined,
      data: new Blob([response.data], {
        type: (response.headers["content-type"] as string) || "application/octet-stream",
      }),
    };
  } catch (error) {
    throw error;
  }
};

export const downloadFileFromServer = async (
  url: string | null,
  container: string | null,
  filename: string | null,
  bucket_name?: string | null,
): Promise<APIBlobResponse> => {
  try {
    const config = postConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );

    let data: any = {};
    if (url) {
      data.url = url;
    }
    if (container) {
      data.container = container;
    }
    if (filename) {
      data.file_name = filename;
    }
    if (bucket_name) {
      data.bucket_name = bucket_name;
    }

    const response = await axios({
      ...config,
      url: `${config.baseUrl}/v5/files/download`,
      responseType: "blob",
      data,
    });

    const disposition = response.headers["content-disposition"];

    let apiFilename;

    if (disposition) {
      const utfMatch = disposition.match(/filename\*=UTF-8''(.+)/);
      const asciiMatch = disposition.match(/filename="?([^"]+)"?/);

      if (utfMatch) {
        apiFilename = decodeURIComponent(utfMatch[1]);
      } else if (asciiMatch) {
        apiFilename = asciiMatch[1];
      }
    }

    return {
      filename: apiFilename,
      data: new Blob([response.data], {
        type: (response.headers["content-type"] as string) || "application/octet-stream",
      }),
    };
  } catch (error) {
    throw error;
  }
};

export const downloadSecureFile = async (
  fileUrl: string,
  defaultFilename?: string,
): Promise<APIBlobResponse> => {
  try {
    const config = postConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );

    const response = await axios({
      ...config,
      url: `${config.baseUrl}/v5/files/secure/download`,
      responseType: "blob",
      data: { url: fileUrl },
    });

    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];
    const list = contentDisposition?.split("=") || [];
    const filename =
      list.length > 1
        ? list[1].replace(/"/g, "").trim()
        : defaultFilename || "download";

    return {
      filename,
      data: new Blob([response.data], {
        type: (response.headers["content-type"] as string) || "application/octet-stream",
      }),
    };
  } catch (error) {
    throw error;
  }
};

export const uploadCSVAPI = async (
  url: string,
  file: File,
): Promise<APIResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const config = uploadFileConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      data: formData,
    });

    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const uploadSingleFile = async (
  url: string,
  file: File,
  container: string,
): Promise<APIResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("container", container);

  try {
    const config = uploadFileConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      data: formData,
    });

    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const uploadMultipleFiles = async (
  url: string,
  files: File[],
  container: string,
): Promise<APIResponse> => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  if (container) {
    formData.append("container", container);
  }
  try {
    const config = uploadFileConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      data: formData,
    });

    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};

export const uploadPrintingFiles = async (
  url: string,
  files: File[],
): Promise<APIResponse> => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  formData.append("container", "printing-files");
  try {
    const config = uploadFileConfig(
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || ""),
    );
    const response = await axios({
      ...config,
      url: `${config.baseUrl}${url}`,
      data: formData,
    });

    return response.data as APIResponse;
  } catch (error) {
    throw error;
  }
};
